/* eslint-disable no-empty-pattern */
import debounce from 'lodash/debounce';

import { MODULE_NAME } from './const';
import { userOnBoardingMetadata, userFavoriteAppIds, allCalculatedDirectoryAppsIds } from './getters';
import {
	SET_LOADING,
	LOGIN,
	LOGOUT,
	USER,
	SET_IS_USER_REGISTRATION_LOADING,
	SET_PUB_SUB_TOKEN,
	SET_PUB_SUB_REFRESH_TOKEN,
	SET_IS_LOADING_USER_DATA,
} from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { INDEPENDENT_RESOURCES, USER_RESOURCES, USER_RESOURCES_NO_NAMESPACE } from '../StoreModuleService/store-module-service.const';
import { setActiveAccountId, setUserAccounts } from '../account/actions';
import { getAvailableApps, getServiceActionCollections, getAllDirectoryApps } from '../apps/actions';
import {
	getEntitlementPlans,
	getEntitlements,
	getProducts,
	subscribeToChanges as subscribeToAccountBillingChanges,
} from '../billing/actions';
import { getUserCredentials } from '../credentials/actions';
import { fetchCustomDomains } from '../customDomains/actions';
import { isExtensionLogin, isInExtensionIframe } from '../main/getters';
import { setNativeIntegrationAccounts } from '../nativeIntegrationAccounts/actions';
import { getAllProjects } from '../projects/actions';
import api from '@/api';
import BroadcastChannelService from '@/broadcastChannel/broadcast-channel-service.js';
import { EVENTS } from '@/broadcastChannel/broadcast-channel.const';
import credentials from '@/credentials';
import router from '@/router';
import RouterService from '@/router/router-service';
import { WORKSPACE_SELECTION_ROUTE_NAME } from '@/router/router.const.js';
import { getAccountMembers } from '@/store/modules/account/actions';
import { startPollingAlerts, stopPollingAlerts, getAlertsStats } from '@/store/modules/alerts/actions';
import { getAppsWorkspaces } from '@/store/modules/workflows/actions';
import AnalyticsService from '@/utils/AnalyticsService';
import BrowserService from '@/utils/BrowserService';
import { useCache } from '@/utils/cache';
import ErrorHandlingService, { STATUS_CODES, LOGOUT_REASON } from '@/utils/error-handling-service';
import { SET_TOKEN_FROM_IFRAME } from '@/utils/extension-messages-listener-service.const.js';
import FeatureToggleService from '@/utils/feature-toggle-service';
import { isNullOrUndefinedOrEmpty, getRoutePrefixName, isValidRedirectURL } from '@/utils/helpers';
import PubSub, { PUB_SUB_EVENTS } from '@/utils/pub-sub-service';
import pusherService from '@/utils/pusher';
import UserAgentService from '@/utils/user-agent-service';

const buildAuthActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action, false);
const EMAIL_ALREADY_EXISTS_ERROR = 'user-0001';
const { clearCache } = useCache();

if (state.isLoggedIn) api.setToken(credentials.token);

// actions declarations
export const initUser = buildAuthActionObject('initUser');
export const verifyOTP = buildAuthActionObject('verifyOTP');
export const forgotPassword = buildAuthActionObject('forgotPassword');
export const resetPassword = buildAuthActionObject('resetPassword');
export const changePassword = buildAuthActionObject('changePassword');
export const resendConfirmationEmail = buildAuthActionObject('resendConfirmationEmail');
export const validateReset = buildAuthActionObject('validateReset');
export const loginWithToken = buildAuthActionObject('loginWithToken');
export const _loginWithUserToken = buildAuthActionObject('_loginWithUserToken');
export const postLogin = buildAuthActionObject('postLogin');
export const afterSuccessfulLogin = buildAuthActionObject('afterSuccessfulLogin');
export const loginWithGoogleToken = buildAuthActionObject('loginWithGoogleToken');
export const registerUser = buildAuthActionObject('registerUser');
export const logout = buildAuthActionObject('logout');
export const loginWithUserPass = buildAuthActionObject('loginWithUserPass');
export const getUser = buildAuthActionObject('getUser');
export const getAllUserData = buildAuthActionObject('getAllUserData');
export const saveUserProfile = buildAuthActionObject('saveUserProfile');
export const saveUserProfileAvatar = buildAuthActionObject('saveUserProfileAvatar');
export const verifyEmailAddress = buildAuthActionObject('verifyEmailAddress');
export const saveCredentials = buildAuthActionObject('saveCredentials');
export const sendCredsToExtension = buildAuthActionObject('sendCredsToExtension');
export const resetUserData = buildAuthActionObject('resetUserData');
export const saveUserMetadata = buildAuthActionObject('saveUserMetadata');
export const saveUserOnBoardingMetadata = buildAuthActionObject('saveUserOnBoardingMetadata');
export const markOnBoardingStepAsComplete = buildAuthActionObject('markOnBoardingStepAsComplete');
export const createWorkspace = buildAuthActionObject('createWorkspace');
export const joinWorkspace = buildAuthActionObject('joinWorkspace');
export const setIsUserRegistrationLoading = buildAuthActionObject('setIsUserRegistrationLoading');
export const verifyOneTimeToken = buildAuthActionObject('verifyOneTimeToken');
export const getOneTimeToken = buildAuthActionObject('getOneTimeToken');
export const saveFavoriteApps = buildAuthActionObject('saveFavoriteApps');
export const deleteFavoriteApp = buildAuthActionObject('deleteFavoriteApp');
export const requestFavoriteApp = buildAuthActionObject('requestFavoriteApp');
export const deleteFavoriteAppRequest = buildAuthActionObject('deleteFavoriteAppRequest');
export const getUserFavoriteApps = buildAuthActionObject('getUserFavoriteApps');
export const getUserCalculatedFavoriteApps = buildAuthActionObject('getUserCalculatedFavoriteApps');
export const subscribeToInvalidToken = buildAuthActionObject('subscribeToInvalidToken');
export const unsubscribeToInvalidToken = buildAuthActionObject('unsubscribeToInvalidToken');
export const subscribeToRefreshToken = buildAuthActionObject('subscribeToRefreshToken');
export const unsubscribeToRefreshToken = buildAuthActionObject('unsubscribeToRefreshToken');
export const initCredentials = buildAuthActionObject('initCredentials');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [initCredentials.action]({ dispatch }, { creds }) {
		credentials.set(creds);
		await dispatch(saveCredentials.action, { creds });
	},
	async [initUser.action]({ dispatch }, payload = {}) {
		if (credentials.user && credentials.token) {
			dispatch(subscribeToInvalidToken.action);
			dispatch(subscribeToRefreshToken.action);
			return dispatch(getUser.action, { id: credentials.user })
				.then(() => {
					if (credentials.account || state.user?.profile?.account) {
						const ftsUser = FeatureToggleService.buildFtsUserObject(
							state.user.id,
							state.user.email,
							state.user.first_name,
							state.user.last_name,
							state.user.custom_fields
						);
						FeatureToggleService.initializeUser(ftsUser);
						dispatch(saveCredentials.action, { creds: credentials });
						dispatch(getAllUserData.action);
						pusherService.setPusherForUser(credentials.token, credentials.account, credentials.user);
					}
				})
				.catch((err) => {
					if (err?.response?.status === STATUS_CODES.UNAUTHORIZED || err?.response?.status === STATUS_CODES.NOT_FOUND) {
						dispatch(logout.action, { avoidRedirect: payload.avoidRedirect });
					} else {
						throw err;
					}
				});
		} else {
			dispatch(logout.action, { avoidRedirect: payload.avoidRedirect });
		}
	},
	async [forgotPassword.action]({}, { email }) {
		const ret = await api.forgotPassword(email);
		return ret;
	},
	async [getUserFavoriteApps.action]({ dispatch, getters }) {
		return dispatch(getAllDirectoryApps.fullPath, { specificIds: getters[userFavoriteAppIds.getter] }, { root: true });
	},
	async [getUserCalculatedFavoriteApps.action]({ dispatch, getters }) {
		return dispatch(getAllDirectoryApps.fullPath, { specificIds: getters[allCalculatedDirectoryAppsIds.getter] }, { root: true });
	},
	async [resetPassword.action]({}, { userID, token, newPassword, newPasswordConfirmation }) {
		const ret = await api.resetPassword(userID, token, newPassword, newPasswordConfirmation);
		return ret;
	},
	async [changePassword.action]({}, { userID, currentPassword, password, password2 }) {
		const ret = await api.changePassword(userID, currentPassword, password, password2);
		return ret;
	},
	async [resendConfirmationEmail.action]({}, { userEmail }) {
		return await api.resendConfirmationEmail(userEmail);
	},
	async [validateReset.action]({}, { userID, token }) {
		const ret = await api.validateReset(userID, token);
		return ret;
	},
	// This is being called after a successful login
	// Should handle return_to query param if available, otherwise redirect to path '/'
	async [afterSuccessfulLogin.action]({}) {
		const qs = router.currentRoute.query;

		const maybeNextURL = qs.return_to || qs.next;
		let nextURL;
		if (maybeNextURL && isValidRedirectURL(maybeNextURL)) {
			nextURL = maybeNextURL;
		}

		const user = state.user;
		const userProfile = user.profile || {};
		const userAccounts = userProfile.user_accounts || [];
		const doesUserHaveAccount = Boolean(userProfile.account);
		const isUserAccountOwner = userAccounts.some((account) => account.account.owner.id === user.id);
		const accountAlreadyGotData = !isNullOrUndefinedOrEmpty(userProfile.account?.name);
		const didUserAnswerDetailsForm = Boolean(
			userProfile.metadata?.userDetails || userProfile.metadata?.companyName || accountAlreadyGotData
		);
		const doesUserHavePendingInvitation = userAccounts.some((account) => account.is_invitation_pending);
		const isInvitedUserWithoutAccount = Boolean(!didUserAnswerDetailsForm && doesUserHavePendingInvitation && !doesUserHaveAccount);

		ErrorHandlingService.setUser(user);
		ErrorHandlingService.setUserMetadata(user);
		const routeSuffixName = getRoutePrefixName();
		if (window.location.pathname === '/invited-user-signup/' || isInvitedUserWithoutAccount || !doesUserHaveAccount) {
			router.push({ name: routeSuffixName + WORKSPACE_SELECTION_ROUTE_NAME, query: qs });
		} else if (!didUserAnswerDetailsForm && isUserAccountOwner) {
			router.push({ name: routeSuffixName + 'UserDetailsForm', query: qs, params: { userEmail: user.email } });
		} else if (nextURL) {
			window.location = nextURL;
		} else if (!UserAgentService.isUsingChromium()) {
			router.push({ name: routeSuffixName + 'InstallChrome' });
		} else {
			router.push({ name: 'HomePage' });
		}
	},

	async [loginWithGoogleToken.action]({ dispatch }, { token, isInstallable }) {
		return api.loginWithGoogleToken(token, isInstallable).then((data) => {
			const { token, error, success } = data;
			if (error) {
				throw new Error(error);
			}
			if (success) {
				dispatch(loginWithToken.action, token);
			}
			return { token, success };
		});
	},
	async [registerUser.action]({ dispatch }, payload) {
		try {
			const { email, firstName, lastName, password, signupCode, signupReferrer, uid, metadata, timezone, gtoken, isInstallable } = payload;
			const profile = { metadata, timezone };
			const result = await api.registerUser(
				email,
				firstName,
				lastName,
				password,
				signupCode,
				signupReferrer,
				uid,
				profile,
				gtoken,
				isInstallable
			);
			// We use verify email method
			if (result && result.token) {
				await dispatch(loginWithToken.action, result.token);
			} else {
				// Identity in Analytics
				AnalyticsService.identifyUser(result);
			}
			AnalyticsService.updateUserProps({ email, firstName, lastName, signupReferrer });
			return result;
		} catch (err) {
			console.error(err);
			const { data: res } = err.response;
			if (res.error.code === EMAIL_ALREADY_EXISTS_ERROR && !res.is_email_verified) {
				router.push({ name: 'SignedUpSuccessfull', params: { userEmail: payload.email, resendEmail: true } });
			} else {
				throw err;
			}
		}
	},
	async [logout.action]({ commit, dispatch }, payload = {}) {
		try {
			await api.logout();
		} catch (err) {
			console.error(err);
		}

		credentials.clear();
		dispatch(stopPollingAlerts.fullPath, {}, { root: true });
		pusherService.clearPusher();
		commit(LOGOUT, null, { root: true });
		BrowserService.sendToExtensions({ action: 'removeToken', value: payload }, () => {});
		FeatureToggleService.clearUser();
		dispatch(resetUserData.action);
		dispatch(unsubscribeToInvalidToken.action);
		dispatch(unsubscribeToRefreshToken.action);
		if (!payload.fromBroadcastChannel) {
			BroadcastChannelService.postBroadcastMessage(EVENTS.LOGOUT); // notify other tabs
		}
		ErrorHandlingService.clear();
		if (!payload.avoidRedirect) {
			// redirect to login for login purposes but keep the location we were just at so we know how to return back
			const returnTo = BroadcastChannelService.calculateLogoutReturnTo(router, payload.fromBroadcastChannel);
			const loginRoute = { name: 'Login' };
			if (router.currentRoute.name && !['logout', 'login'].includes(router.currentRoute.name.toLowerCase())) {
				loginRoute.query = { ...router.currentRoute.query, return_to: returnTo };
			}
			router.push(loginRoute);
		}
	},
	async [loginWithUserPass.action]({ commit, dispatch }, { username, password }) {
		commit(SET_LOADING, true);
		try {
			const ret = await api.loginWithUserPass(username, password);
			dispatch(postLogin.action, ret);
		} catch (error) {
			console.log('error is', error);
			commit(SET_LOADING, false);
			if (ErrorHandlingService.extractErrorMessage(error)) {
				return ErrorHandlingService.extractErrorMessage(error);
			}
			const { response: res } = error;
			return res.data.non_field_errors[0];
		}
	},
	async [postLogin.action](
		{ commit, dispatch },
		{ token, refresh, user_id: user, active_account_id: account, avoidRedirect, fromBroadcastChannel }
	) {
		await dispatch(resetUserData.action);
		await dispatch(initCredentials.action, { creds: { user, token, refresh, account } });
		if (!fromBroadcastChannel) {
			BroadcastChannelService.postBroadcastMessage(EVENTS.LOGIN, credentials); // notify other tabs
			const isSelectingWorkspaceInExtensionIframe = RouterService.isExtensionWorkspaceSelectionRoute(router.currentRoute.name);
			if (isSelectingWorkspaceInExtensionIframe) {
				BrowserService.sendToExtensions({ action: SET_TOKEN_FROM_IFRAME, value: credentials });
			}
		}
		commit(SET_LOADING, false);
		await dispatch(initUser.action);

		if (state.isLoggedIn || fromBroadcastChannel) {
			dispatch(setUserAccounts.fullPath, { accounts: state.user?.profile?.user_accounts }, { root: true });
			if (!avoidRedirect) {
				dispatch(afterSuccessfulLogin.action);
			}
		}
		commit(SET_LOADING, false);
	},
	async [saveFavoriteApps.action]({ commit, getters }, payload) {
		const { favoriteAppsIds, dontKeepPreviousFavorites } = payload;
		let idsToSave = [...favoriteAppsIds];
		if (!dontKeepPreviousFavorites) {
			const userPreviousSelectedFavoriteAppsIds = getters[userFavoriteAppIds.getter];
			idsToSave = [...idsToSave, ...userPreviousSelectedFavoriteAppsIds];
		}
		const user = await api.saveFavoriteApps(state.user.id, idsToSave);
		commit(USER, user);
	},
	async [requestFavoriteApp.action]({ commit }, payload) {
		const { id } = payload;
		const user = await api.requestFavoriteApp(state.user.id, id);
		commit(USER, user);
	},
	async [deleteFavoriteAppRequest.action]({ commit }, payload) {
		const { id } = payload;
		const user = await api.deleteFavoriteAppRequest(state.user.id, id);
		commit(USER, user);
	},
	async [deleteFavoriteApp.action]({ commit, getters }, payload) {
		const { appId } = payload;
		const userPreviousSelectedFavoriteAppsIds = getters[userFavoriteAppIds.getter];
		const idsToSave = userPreviousSelectedFavoriteAppsIds.filter((id) => id !== appId);
		const user = await api.saveFavoriteApps(state.user.id, idsToSave);
		commit(USER, user);
	},
	async [getUser.action]({ commit, dispatch }, payload) {
		const id = payload.id || credentials.user;
		commit(SET_LOADING, true);
		return api
			.getUser(id)
			.then((user) => {
				commit(USER, user);
				if (user.profile.account) {
					dispatch(setActiveAccountId.fullPath, { id: user.profile.account.id }, { root: true });
					dispatch(setUserAccounts.fullPath, { accounts: user.profile.user_accounts }, { root: true });
					dispatch(setNativeIntegrationAccounts.fullPath, { data: user.native_integration_accounts }, { root: true });
				} else {
					console.log('no account, it will redirect from afterSuccessful login to invited user to workspace');
				}
				return user;
			})
			.catch((err) => {
				if (err?.response?.status === STATUS_CODES.UNAUTHORIZED || err?.response?.status === STATUS_CODES.NOT_FOUND) {
					dispatch(logout.action, { logoutReason: LOGOUT_REASON.tokenExpired });
				} else {
					throw err;
				}
			});
	},
	async [getAllUserData.action]({ dispatch, commit }) {
		commit(SET_IS_LOADING_USER_DATA, true);
		try {
			// do not wait for these, just get them so we have it in the background
			dispatch(getEntitlementPlans.fullPath, {}, { root: true });
			dispatch(getProducts.fullPath, {}, { root: true });
			dispatch(fetchCustomDomains.fullPath, { root: true });
			await Promise.all([
				dispatch(getAvailableApps.fullPath, null, { root: true }),
				dispatch(getServiceActionCollections.fullPath, null, { root: true }),
				dispatch(getAllProjects.fullPath, { forceGet: true }, { root: true }),
				dispatch(getUserCredentials.fullPath, { forceGet: true }, { root: true }),
				dispatch(getAccountMembers.fullPath, null, { root: true }),
				dispatch(startPollingAlerts.fullPath, {}, { root: true }),
				dispatch(subscribeToAccountBillingChanges.fullPath, {}, { root: true }),
				dispatch(getAppsWorkspaces.fullPath, { forceGet: true }, { root: true }),
				dispatch(getAlertsStats.fullPath, {}, { root: true }),
				dispatch(getUserFavoriteApps.action, {}),
				dispatch(getEntitlements.fullPath, {}, { root: true }),
			]);
		} finally {
			commit(SET_IS_LOADING_USER_DATA, false);
		}
	},
	async [saveUserProfile.action]({ commit }, userObj) {
		// Todo: Consider updating in Mixpanel\AnalyticsService as well
		const userUpdate = {
			first_name: userObj.first_name,
			last_name: userObj.last_name,
			profile: {
				...userObj.profile,
				comminucation_preferences: userObj.comminucation_preferences,
				metadata: userObj.metadata,
			},
		};
		const ret = await api.saveUserProfile(state.user.id, userUpdate);
		commit(USER, ret);
		return ret;
	},
	async [saveUserProfileAvatar.action]({ commit }, payload) {
		const { avatar } = payload;
		const ret = await api.saveUserProfileAvatar(state.user.id, avatar);
		commit(USER, ret);
		return ret;
	},
	async [verifyEmailAddress.action]({}, payload) {
		const { email } = payload;
		const ret = await api.verifyEmailAddress(email);
		return ret;
	},
	[saveCredentials.action]({ commit, dispatch, rootGetters }, { creds, fromRefresh = false }) {
		const { token, account } = creds;
		commit(LOGIN, token);
		if (!rootGetters[isInExtensionIframe.fullPath] && account) {
			// send an update to the ext only if we finished the loop of the login successfully,
			// otherwise it will get invalid token without the account
			dispatch(sendCredsToExtension.action, { creds, fromRefresh });
		}
	},
	[sendCredsToExtension.action]: debounce(({ rootGetters }, { creds, fromRefresh }) => {
		if (window.chrome && window.chrome.runtime) {
			const handleResponse = () => {
				if (rootGetters[isExtensionLogin.fullPath] && window.opener) {
					window.opener.postMessage({ action: 'loginCompleted' }, '*');
				}
			};
			BrowserService.sendToExtensions({ action: 'setToken', value: creds, fromRefresh }, handleResponse);
		}
	}, 500),
	[resetUserData.action]({ dispatch }) {
		// reset independent resources
		return Promise.all([
			new Promise((resolve) => {
				clearCache();
				resolve();
			}),
			// reset independent resources
			...StoreModuleService.resetResources(dispatch, INDEPENDENT_RESOURCES),
			// reset user resources
			...StoreModuleService.resetResources(dispatch, USER_RESOURCES),
			...StoreModuleService.resetResources(dispatch, USER_RESOURCES_NO_NAMESPACE, false),
		]);
	},
	async [saveUserMetadata.action]({ dispatch }, payload) {
		// This doesn't override the whole metadata object, it only updates the given keys
		// this happens in the backend
		try {
			const ret = await api.saveUserMetadata(state.user.id, payload);
			await dispatch(getUser.action, { id: state.user.id });
			return ret;
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [saveUserOnBoardingMetadata.action]({ dispatch, getters }, payload) {
		const currentUserOnBoardingMetadata = getters[userOnBoardingMetadata.getter];
		try {
			const newUserOnBoardingMetadata = { ...currentUserOnBoardingMetadata, ...payload };
			return await dispatch(saveUserMetadata.action, { onBoarding: newUserOnBoardingMetadata });
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [markOnBoardingStepAsComplete.action]({ dispatch, getters }, payload) {
		const currentOnBoardingObj = getters[userOnBoardingMetadata.getter];
		const newId = payload?.id;
		try {
			const newOnBoardingObj = { ...currentOnBoardingObj, completedStepsIds: [...(currentOnBoardingObj.completedStepsIds || []), newId] };
			return await dispatch(saveUserMetadata.action, { onBoarding: newOnBoardingObj });
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [createWorkspace.action]({ dispatch }, payload) {
		try {
			// empty id to joinWorkspace creates workspace
			return await dispatch(joinWorkspace.action, {
				userId: state.user.id,
				accountLinkId: null,
				name: payload.name,
				companySize: payload.companySize,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [joinWorkspace.action]({ dispatch }, payload) {
		const { userId, accountLinkId, name, companySize, timezone } = payload;
		const isSwitchingBetweenTwoWorkspaces = state.user?.profile?.account;
		const ret = await api.joinWorkspace(userId, accountLinkId, name, companySize, timezone);
		await dispatch(postLogin.action, { ...ret, avoidRedirect: isSwitchingBetweenTwoWorkspaces });
		return ret;
	},
	async [setIsUserRegistrationLoading.action]({ commit }, payload) {
		commit(SET_IS_USER_REGISTRATION_LOADING, payload);
	},
	async [loginWithToken.action]({ dispatch }, u_token) {
		await dispatch(_loginWithUserToken.action, { func: api.loginWithToken, u_token });
	},
	async [verifyOneTimeToken.action]({ dispatch }, { token, email }) {
		try {
			await dispatch(_loginWithUserToken.action, { func: api.verifyOneTimeToken, u_token: { token, email } });
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [verifyOTP.action]({ dispatch }, payload) {
		try {
			const { otp, email } = payload;
			const res = await api.verifyOTP(otp, email);
			dispatch(postLogin.action, res);
			return res;
		} catch (err) {
			console.error(err);
			return false;
		}
	},
	async [getOneTimeToken.action]({}) {
		try {
			const ret = await api.getOneTimeToken();
			return ret;
		} catch (err) {
			console.error(err);
			return null;
		}
	},
	async [_loginWithUserToken.action]({ commit, dispatch }, { func, u_token }) {
		commit(SET_LOADING, true);
		dispatch(subscribeToInvalidToken.action);
		dispatch(subscribeToRefreshToken.action);
		try {
			const data = await func(u_token);
			dispatch(postLogin.action, data);
		} finally {
			commit(SET_LOADING, false);
		}
	},
	[subscribeToInvalidToken.action]({ dispatch, commit }) {
		if (!state.pubSubToken) {
			const token = PubSub.subscribe(PUB_SUB_EVENTS.TOKEN_INVALID, (_, payload) => {
				console.log('token invalid!!');
				if (state.isLoggedIn) {
					dispatch(logout.action, payload);
				}
			});
			commit(SET_PUB_SUB_TOKEN, token);
		}
	},
	[unsubscribeToInvalidToken.action]({ commit }) {
		if (state.pubSubToken) {
			PubSub.unsubscribe(state.pubSubToken);
			commit(SET_PUB_SUB_TOKEN, null);
		}
	},
	[subscribeToRefreshToken.action]({ commit, dispatch }) {
		if (!state.pubSubRefreshToken) {
			const pubSubRefreshToken = PubSub.subscribe(PUB_SUB_EVENTS.REFRESH_TOKEN, async (topic, { promise, refreshToken }) => {
				try {
					const { token, refresh } = await refreshToken(credentials.refresh);
					credentials.setRefresh({ token, refresh });
					api.setToken(credentials.token);
					pusherService.refreshPusherToken(token);
					await dispatch(saveCredentials.action, { creds: credentials, fromRefresh: true });
					promise.resolve(token);
				} catch {
					promise.reject();
				}
			});
			commit(SET_PUB_SUB_REFRESH_TOKEN, pubSubRefreshToken);
		}
	},
	[unsubscribeToRefreshToken.action]({ commit }) {
		if (state.pubSubRefreshToken) {
			PubSub.unsubscribe(state.pubSubRefreshToken);
			commit(SET_PUB_SUB_REFRESH_TOKEN, null);
		}
	},
};

export default actions;
